import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { TextField } from '@/components/ui/TextField'
import { useMe } from '@/features/auth/api'
import { useBanMember, useKickMember, useLoungeMembers, useLounge } from '@/features/lounges/api'
import { useNooks } from '@/features/nooks/api'
import { type Member, partition } from '@/features/presence/FacePile'
import type { Role } from '@/gql/graphql'

import '@/features/presence/presence.css'

/**
 * Who's Home — the whole lounge, grouped by who is actually here.
 *
 * **A ratified surface-pattern change**: the member list moved from a side panel
 * to a modal. The panel was reference material permanently competing for width
 * with the room; this is a thing you open, read, and close. Kick and ban came
 * with it, because two surfaces listing the same people is exactly what the
 * retheme set out to remove.
 */
export const Route = createFileRoute('/_app/l/$loungeSlug/roster')({
  component: Roster,
})

function Roster() {
  const { loungeSlug } = Route.useParams()
  const navigate = useNavigate()
  const { data: loungeData } = useLounge(loungeSlug)
  const lounge = loungeData?.lounge
  const { data } = useLoungeMembers(lounge?.id ?? '')
  const { data: me } = useMe()

  const [pending, setPending] = useState<{
    kind: 'kick' | 'ban'
    userId: string
    displayName: string
  } | null>(null)
  const [reason, setReason] = useState('')

  const kick = useKickMember(lounge?.id ?? '')
  const ban = useBanMember(lounge?.id ?? '')

  const close = () => void navigate({ to: '/l/$loungeSlug', params: { loungeSlug } })

  if (!lounge) return null

  const members = data?.loungeMembers ?? []
  const { online, offline } = partition(members)

  return (
    <>
      <Dialog
        open={pending === null}
        onClose={close}
        title="Who's Home"
        description={census(online.length, members.length)}
      >
        <div className="roster">
          {online.length > 0 && (
            <RosterGroup
              label="Here now"
              members={online}
              loungeId={lounge.id}
              myId={me?.me?.id}
              myRole={lounge.myRole}
              onAct={setPending}
            />
          )}
          {offline.length > 0 && (
            <RosterGroup
              label="Away"
              members={offline}
              loungeId={lounge.id}
              myId={me?.me?.id}
              myRole={lounge.myRole}
              onAct={setPending}
            />
          )}
        </div>
      </Dialog>

      {/* Replaces the roster rather than stacking on it — one modal at a time. */}
      <ConfirmDialog
        open={pending !== null}
        onCancel={() => {
          setPending(null)
          setReason('')
        }}
        onConfirm={() => {
          if (!pending) return
          const done = () => {
            setPending(null)
            setReason('')
          }
          if (pending.kind === 'ban') ban.mutate({ userId: pending.userId, reason }, { onSuccess: done })
          else kick.mutate(pending.userId, { onSuccess: done })
        }}
        pending={kick.isPending || ban.isPending}
        title={
          pending?.kind === 'ban' ? `Ban ${pending.displayName}?` : `Kick ${pending?.displayName}?`
        }
        description={
          pending?.kind === 'ban'
            ? 'They lose access and cannot rejoin, even with an invite link, until the ban is lifted.'
            : 'They lose access to this lounge, but can be invited back.'
        }
        confirmLabel={pending?.kind === 'ban' ? 'Ban' : 'Kick'}
      >
        {/* Optional, and it goes in the audit log — which outlives the ban row,
         * so the record of why survives the ban being lifted. */}
        {pending?.kind === 'ban' && (
          <TextField
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint="Optional. Kept in the audit log, and not shown to them."
          />
        )}
      </ConfirmDialog>
    </>
  )
}

function RosterGroup({
  label,
  members,
  loungeId,
  myId,
  myRole,
  onAct,
}: {
  label: string
  members: Member[]
  loungeId: string
  myId: string | undefined
  myRole: Role | null | undefined
  onAct: (target: { kind: 'kick' | 'ban'; userId: string; displayName: string }) => void
}) {
  // Where anybody in this lounge is sitting, from the nook list the sidebar is
  // already drawing and `voicePresence` is already keeping live.
  //
  // Built from *this lounge's* cached nooks rather than asked of the server,
  // and that is not only thrift: `nook_of` answers globally, so a server-side
  // join would happily say which room somebody is in inside a lounge you are
  // not a member of. This way you can only ever learn about rooms you can
  // already see.
  const { data } = useNooks(loungeId)
  const inVoice = new Map<string, string>()
  for (const nook of data?.nooks ?? []) {
    for (const seat of nook.voiceParticipants) inVoice.set(seat.user.id, nook.name)
  }

  return (
    <section className="roster-group">
      <h3 className="roster-group-label">{label}</h3>
      <ul className="roster-list">
        {members.map((member) => (
          <li key={member.user.id} className="roster-row">
            <span className="roster-face" data-away={member.presence === 'ONLINE' ? undefined : true}>
              <Avatar
                displayName={member.user.displayName}
                avatarUrl={member.user.avatarUrl}
                size={2.25}
              />
              <span
                className="roster-pip"
                data-presence={member.presence.toLowerCase()}
                aria-hidden="true"
              />
            </span>

            <div className="roster-who">
              <p className="roster-name">{member.user.displayName}</p>
              <p className="roster-note">{note(member, inVoice.get(member.user.id))}</p>
            </div>

            {canRemove(myRole, member.role, member.user.id, myId) && (
              <div className="roster-actions">
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() =>
                    onAct({
                      kind: 'kick',
                      userId: member.user.id,
                      displayName: member.user.displayName,
                    })
                  }
                >
                  Kick
                </button>
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() =>
                    onAct({
                      kind: 'ban',
                      userId: member.user.id,
                      displayName: member.user.displayName,
                    })
                  }
                >
                  Ban
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The one line under a name. Where they are beats what they are. */
function note(member: Member, nookName: string | undefined): string {
  if (nookName) return `In ${nookName}`
  if (member.presence === 'IDLE') return 'Idle'
  if (member.presence === 'OFFLINE') return member.role === 'MEMBER' ? 'Away' : roleWord(member.role)
  return member.role === 'MEMBER' ? 'Here' : roleWord(member.role)
}

function roleWord(role: Role): string {
  return role === 'OWNER' ? 'Owner' : role === 'MOD' ? 'Moderator' : 'Member'
}

/**
 * One whole sentence per case rather than two halves glued with a separator —
 * the i18n rule, applied where gluing was most tempting.
 */
function census(online: number, total: number): string {
  if (total === 1) return 'Just you so far'
  if (online === 1) return `One person here · ${total} members`
  return `${online} people here · ${total} members`
}

const rank = (role: Role) => (role === 'OWNER' ? 2 : role === 'MOD' ? 1 : 0)

function canRemove(
  myRole: Role | null | undefined,
  targetRole: Role,
  targetId: string,
  myId: string | undefined,
): boolean {
  if (!myRole || rank(myRole) < 1) return false
  if (targetId === myId) return false
  return rank(myRole) > rank(targetRole)
}
